export type UiDensity =
  | "ultraCompact"
  | "extraCompact"
  | "compact"
  | "snug"
  | "normal"
  | "comfortable";

/** Smallest → largest. Index 4 is the pre-density default ("normal"). */
export const DENSITY_LEVELS: readonly UiDensity[] = [
  "ultraCompact",
  "extraCompact",
  "compact",
  "snug",
  "normal",
  "comfortable",
];
export const DEFAULT_DENSITY: UiDensity = "normal";
export const DENSITY_MIN_INDEX = 0;
export const DENSITY_MAX_INDEX = DENSITY_LEVELS.length - 1;

// Mirrors theme.breakpoints.down("md"); MUI's default md breakpoint is 900px.
// Kept as a literal so it can be read before a theme exists.
export const MOBILE_MEDIA_QUERY = "(max-width:899.95px)";

export interface DensityTokens {
  typographyFontSize: number;
  spacing: number;
  tableCellPaddingY: number;
  tableHeadPaddingY: number;
}

// "normal" reproduces the pre-density defaults exactly.
export const DENSITY_TOKENS: Record<UiDensity, DensityTokens> = {
  ultraCompact: {
    typographyFontSize: 10.5,
    spacing: 5,
    tableCellPaddingY: 0,
    tableHeadPaddingY: 3,
  },
  extraCompact: {
    typographyFontSize: 11.5,
    spacing: 6,
    tableCellPaddingY: 1,
    tableHeadPaddingY: 4,
  },
  compact: { typographyFontSize: 12.5, spacing: 7, tableCellPaddingY: 2, tableHeadPaddingY: 6 },
  snug: { typographyFontSize: 13.25, spacing: 7, tableCellPaddingY: 3, tableHeadPaddingY: 8 },
  normal: { typographyFontSize: 14, spacing: 8, tableCellPaddingY: 4, tableHeadPaddingY: 10 },
  comfortable: { typographyFontSize: 15, spacing: 9, tableCellPaddingY: 8, tableHeadPaddingY: 14 },
};

export const isUiDensity = (value: unknown): value is UiDensity =>
  typeof value === "string" && DENSITY_LEVELS.includes(value as UiDensity);

export const densityToIndex = (level: UiDensity): number => {
  const index = DENSITY_LEVELS.indexOf(level);
  return index === -1 ? DENSITY_LEVELS.indexOf(DEFAULT_DENSITY) : index;
};

export const indexToDensity = (index: number): UiDensity => {
  const clamped = Math.max(DENSITY_MIN_INDEX, Math.min(DENSITY_MAX_INDEX, Math.round(index)));
  return DENSITY_LEVELS[clamped];
};
